<template>
  <div class="box" v-if="!showFieldEditor">
    <div class="headline">
      <div class="top">Step 4 of 5</div>
      {{ title }}
    </div>
    <b-form>
      <b-form-group
        label="Name"
        description="Model name, capitalized - must be a valid Python class name, for example: Book"
        class="required"
        :invalidFeedback="errorName"
        :state="stateName"
      >
        <b-form-input v-model="model.name" placeholder="example: Book" required :state="stateName"></b-form-input>
      </b-form-group>

      <b-form-group label="Description (docstring)" description="One-line description of the model to put in its docstring">
        <b-form-input v-model="model.docstring"></b-form-input>
      </b-form-group>

      <b-form-group v-if="recipe.django_admin" description="Whether to register the model in Django admin interface">
        <b-form-checkbox v-model="useAdmin">Register in Django admin</b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="userRelationsList" label="Owner" description="Which field, if any, represents the user owning an instance of this model">
        <b-form-select v-model="model.owner_field" :options="userRelationsList"></b-form-select>
      </b-form-group>

      <b-form-group v-if="stringFields" label="String representation" description="Which field, if any, should be the string representation of the object (__str__)">
        <b-form-select v-model="model.str_field" :options="stringFields"></b-form-select>
      </b-form-group>
    </b-form>
    <div class="required font-weight-bold">Fields</div>
    <div v-if="model.fields.length">
      <ul class="models-list">
        <li key="id">
          <code>id</code>
          <em>primary key</em>
        </li>
        <li v-for="field in model.fields" :key="field.name">
          <code>{{ field.name }}</code>
          <em>{{ field.type }}<span v-if="field.target" class="ml-1" style="font-size: 1em;">→ {{ field.target }}</span></em>
          <a href="#" @click.prevent="editField(field)">(edit)</a>
        </li>
      </ul>
    </div>
    <div v-else :class="this.errors.fields ? 'text-danger' : ''">
      You have no custom fields defined. A model should have at least one custom field
      (in addition to the <code>id</code> primary key included by default).
    </div>
    <div class="text-right">
      <b-button size="sm" @click.prevent="editField()">
        <font-awesome-icon icon="plus" />
        Add field
      </b-button>
    </div>
    <div class="button-row">
      <b-button @click="$emit('cancel')">Cancel</b-button>
      <b-button v-if="!isNew" variant="danger" v-b-modal.confirmRemove>Remove model</b-button>
      <b-button variant="primary" @click="applyModelChanges">Apply</b-button>
    </div>

    <b-modal id="confirmRemove" title="Remove Model" @ok="removeModel" ok-title="Remove" ok-variant="danger">
      <p>
        Really remove model <em>{{ model.name }}</em>?
      </p>
    </b-modal>

    <b-modal
      id="askStr"
      ref="askStr"
      title="String representation"
      @ok="confirmStrField"
      ok-title="Use field"
      @cancel="cancelStrField"
      cancel-title="No"
    >
      <p>
        Do you want to use field <code>{{ suggestedStrField }}</code> as the string representation (<code>__str__</code>)
        of the model?
      </p>
    </b-modal>

    <b-modal
      id="askOwner"
      ref="askOwner"
      title="Instance owner"
      @ok="confirmOwnerField"
      ok-title="Yes"
      @cancel="cancelOwnerField"
      cancel-title="No"
    >
      <p>
        Does field <code>{{ suggestedOwnerField }}</code> point to the user owning the instance of {{ model.name }}?
      </p>
    </b-modal>

  </div>
  <div v-else>
    <Field
      :recipe="recipe"
      :model="model"
      :useAdmin="recipe.django_admin && useAdmin"
      :instance="editedField"
      @cancel="clearEditedField"
      @create="onFieldCreate($event)"
      @update="onFieldUpdate($event)"
      @remove="removeField($event)"
    />
  </div>
</template>

<script>

import Vue from 'vue'

import {
  EMPTY_MODEL,
  SINGLE_REL_FIELDS,
  TEXT_FIELDS
} from '../../helpers/constants'

import { toValidClassName } from '../../helpers/python'
import { mapErrorStates } from '../../helpers/errors'
import Field from './Field'

export default {
  name: 'ModelDetail',

  components: {
    Field
  },

  props: {
    recipe: {type: Object, required: true},
    app: {type: Object, required: true},
    instance: {type: Object, required: false}
  },

  data () {
    const isNew = !this.instance
    const model = isNew ? {...EMPTY_MODEL} : JSON.parse(JSON.stringify(this.instance))
    if (isNew) model.name = 'Model' + (this.app.models.length + 1)

    // ensure we have a separate (empty) fields array we can modify
    model.fields = [...model.fields]

    const vf = model.viewset && model.viewset.filter
    if (vf) {
      for (let f of model.fields) {
        f.apiFilter = !!(vf.filter_fields && f.name in vf.filter_fields)
        f.apiSearch = !!(vf.search_fields && f.name in vf.search_fields)
      }
    }

    return {
      isNew,
      model,
      useAdmin: !!model.admin,
      showFieldEditor: false,
      editedField: null,
      suggestedStrField: null,
      suggestedOwnerField: null,
      errors: {}
    }
  },

  computed: {
    title () {
      return this.isNew ? 'Add new model' : 'Edit model ' + this.model.name
    },

    userRelationsList () {
      if (!this.recipe.auth) return null

      let fields = {}
      for (let f of this.model.fields) {
        if (SINGLE_REL_FIELDS.includes(f.type) && f.target === 'users.User' && f.name) {
          fields[f.name] = f.name + ' → ' + f.target
        }
      }
      if (Object.keys(fields).length === 0) return null
      fields[''] = '(none)'
      return fields
    },

    stringFields () {
      let fields = {}
      for (let f of this.model.fields) {
        if (TEXT_FIELDS.includes(f.type) && f.name) {
          fields[f.name] = f.name
        }
      }
      if (Object.keys(fields).length === 0) return null
      fields[''] = '(none)'
      return fields
    },

    references () {
      const refs = []
      // Using instance here since our name might be changing right now
      const myName = `${this.app.name}.${this.instance.name}`

      for (let app of this.recipe.apps) {
        for (let model of app.models) {
          for (let field of model.fields) {
            if (field.target && field.target === myName) {
              refs.push(`${app.name}.${model.name}`)
            }
          }
        }
      }
      return refs
    },

    ...mapErrorStates(['name'])
  },

  watch: {
    'model.name': function () {
      this.model.name = toValidClassName(this.model.name)
    }
  },

  methods: {
    clearEditedField () {
      this.editedField = null
      this.showFieldEditor = false
    },

    onFieldCreate (field) {
      this.model.fields.push(field)
      this.clearEditedField()

      if (this.recipe.auth) {
        if (!this.model.str_field && TEXT_FIELDS.includes(field.type)) {
          this.suggestedStrField = field.name
          Vue.nextTick(() => {
            // need to delay to give Vue chance to bail out of showFieldDialog render branch
            this.$bvModal.show('askStr')
          })
        }

        if (!this.model.owner_field && SINGLE_REL_FIELDS.includes(field.type) && field.target === 'users.User') {
          this.suggestedOwnerField = field.name
          Vue.nextTick(() => {
            // need to delay to give Vue chance to bail out of showFieldDialog render branch
            this.$bvModal.show('askOwner')
          })
        }
      }
    },

    clearFieldReferences () {
      const fieldNames = this.model.fields.map(f => f.name)
      if (this.model.str_field !== null && !fieldNames.includes(this.model.str_field)) this.model.str_field = null
      if (this.model.owner_field !== null && !fieldNames.includes(this.model.owner_field)) this.model.owner_field = null
    },

    onFieldUpdate (field) {
      this.model.fields = this.model.fields.map(f => f === this.editedField ? field : f)
      this.clearEditedField()
      this.clearFieldReferences()
    },

    editField (field) {
      this.editedField = field || null
      this.showFieldEditor = true
    },

    removeField () {
      this.model.fields = this.model.fields.filter(f => f !== this.editedField)
      if (this.model.str_field === this.editedField.name) this.model.str_field = null
      if (this.model.owner_field === this.editedField.name) this.model.owner_field = null
      this.clearFieldReferences()
      this.clearEditedField()
    },

    removeModel () {
      if (this.references.length) {
        const refs = this.references.join(', ')
        this.$bvModal.msgBoxOk('Can\'t remove model, as it is referenced by: ' + refs)
        return
      }

      this.$emit('remove')
    },

    confirmStrField () {
      if (!this.suggestedStrField) return
      this.model.str_field = this.suggestedStrField
      this.suggestedStrField = null
    },

    cancelStrField () {
      this.suggestedStrField = null
    },

    confirmOwnerField () {
      if (!this.suggestedOwnerField) return
      this.model.owner_field = this.suggestedOwnerField
      this.suggestedOwnerField = null
    },

    cancelOwnerField () {
      this.suggestedOwnerField = null
    },

    validate () {
      this.errors = {}
      this.model.name = toValidClassName(this.model.name)
      if (!this.model.name) {
        this.$set(this.errors, 'name', 'A valid Python class name is required')
      }

      for (let m of this.app.models) {
        if (m !== this.instance && m.name === this.model.name) {
          this.$set(this.errors, 'name', 'There is already an model with the same name in the app')
          break
        }
      }

      if (Object.keys(this.model.fields).length === 0) {
        this.$set(this.errors, 'fields', true)
      }

      return Object.keys(this.errors).length === 0
    },

    applyModelChanges () {
      if (!this.validate()) return

      if (!this.recipe.auth || !this.model.owner_field) this.model.owner_field = null
      if (!this.recipe.django_admin) this.useAdmin = false

      if (this.useAdmin) {
        this.model.admin = {
          list_fields: this.model.fields.filter(f => f.adminListField).map(f => f.name),
          search_fields: this.model.fields.filter(f => f.adminSearchBy).map(f => f.name)
        }
      } else {
        this.model.admin = null
      }

      const filterFields = this.model.fields.filter(f => f.apiFilter).map(f => f.name)
      const searchFields = this.model.fields.filter(f => f.apiSearch).map(f => f.name)

      // If we have filter or search fields and we don't have viewset defined, define it
      if ((filterFields.length || searchFields.length) && !this.model.viewset) this.model.viewset = {permissions: {}}
      // If we have viewset, set filter/search fields (this also works if viewset is defined with
      // fields from before and we now want to clear them)
      if (this.model.viewset) {
        this.model.viewset.filter = {
          filter_fields: filterFields,
          search_fields: searchFields,
        }
      }
      const sf = this.model.fields.filter(f => f.includeInSerializer).map(f => f.name)
      if (sf.length) {
        this.model.serializer = {
          fields: sf
        }
      } else {
        this.model.serializer = null
      }

      this.clearFieldReferences()

      this.$emit(this.isNew ? 'create' : 'update', this.model)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/wizard/ModelDetail.vue